import { AosTaskChecklistTemplate } from 'aos-services/src/services/checklists/types/AosTaskChecklistTemplate'
import {
    Box,
    Checkbox,
    CheckboxIndicator,
    ChevronDownGlyph,
    Popover,
    PopoverBackdrop,
    PopoverContent,
    Pressable,
    Text,
} from 'aos-ui-universal'
import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TaskOrTemplatePayload } from '../../../utils'
import { TaskFormField } from '../TaskFormField'

interface TaskChecklistsFieldProps {
    checklists: AosTaskChecklistTemplate[]
}

export const TaskChecklistsField = ({ checklists }: TaskChecklistsFieldProps) => {
    const { t: translate } = useTranslation()
    const { control } = useFormContext<TaskOrTemplatePayload>()
    const [isOpen, setIsOpen] = useState(false)

    return (
        <Controller
            control={control}
            name='checklistsTemplateIds'
            render={({ field }) => {
                const selectedIds = field.value ?? []
                const triggerLabel =
                    selectedIds.length === 0
                        ? translate('form.checklists.placeholder', { ns: 'tasks' })
                        : checklists.length > 0 && selectedIds.length === checklists.length
                          ? translate('form.checklists.all', { ns: 'tasks' })
                          : translate('form.checklists.selected', {
                                count: selectedIds.length,
                                ns: 'tasks',
                            })

                const toggle = (id: number) =>
                    field.onChange(
                        selectedIds.includes(id)
                            ? selectedIds.filter(v => v !== id)
                            : [...selectedIds, id],
                    )

                return (
                    <TaskFormField label={translate('form.checklists.label', { ns: 'tasks' })}>
                        <Popover
                            isOpen={isOpen}
                            onClose={() => setIsOpen(false)}
                            trapFocus={false}
                            focusScope={false}
                            placement='bottom left'
                            trigger={triggerProps => (
                                <Pressable
                                    {...triggerProps}
                                    accessibilityRole='button'
                                    accessibilityState={{ expanded: isOpen }}
                                    onPress={() => setIsOpen(v => !v)}
                                    className='h-12 w-full flex-row items-center justify-between gap-2 rounded border border-solid border-border-subtle bg-background-input p-3 hover:border-border-strong'
                                >
                                    <Text
                                        size='body'
                                        tone={selectedIds.length ? 'default' : 'muted'}
                                    >
                                        {triggerLabel}
                                    </Text>
                                    <ChevronDownGlyph size={24} />
                                </Pressable>
                            )}
                        >
                            <PopoverBackdrop />
                            <PopoverContent className='max-h-64 min-w-[280px] overflow-y-auto rounded border border-solid border-border-subtle bg-background-elevation py-1'>
                                <Box>
                                    {checklists.map(item => {
                                        const isSelected = selectedIds.includes(item.id)
                                        return (
                                            <Pressable
                                                key={item.id}
                                                onPress={() => toggle(item.id)}
                                                accessibilityRole='menuitem'
                                                accessibilityState={{ checked: isSelected }}
                                                className='flex-row items-center gap-3 px-3 py-2 hover:bg-background-surface-hover'
                                            >
                                                <Checkbox
                                                    value={String(item.id)}
                                                    isChecked={isSelected}
                                                    size='sm'
                                                    className='pointer-events-none shrink-0'
                                                >
                                                    <CheckboxIndicator />
                                                </Checkbox>
                                                <Text
                                                    size='body'
                                                    tone='default'
                                                    className='flex-1'
                                                    numberOfLines={1}
                                                >
                                                    {item.name}
                                                </Text>
                                                {item.tag ? (
                                                    <Box className='shrink-0 rounded border border-solid border-border-strong px-2 py-0.5'>
                                                        <Text
                                                            size='caption'
                                                            tone='muted'
                                                            weight='medium'
                                                            className='uppercase'
                                                        >
                                                            {item.tag}
                                                        </Text>
                                                    </Box>
                                                ) : null}
                                            </Pressable>
                                        )
                                    })}
                                </Box>
                            </PopoverContent>
                        </Popover>
                    </TaskFormField>
                )
            }}
        />
    )
}
